//Styles
import { useState } from "react";
// Mui
import { Alert, CircularProgress, Snackbar } from "@mui/material";
// Styles
import Styles from "./styles.module.scss";
// Images
import slideImg from "../../assets/images/therapist_registration_success.jpg";
//Traslation
import { useTranslation } from "react-i18next";
// Routing
import { useNavigate } from "react-router";
import { apiPath } from "../../constants";
//Links
import termsOfUseLink from "../../documents/terms_of_use.pdf";
import privacyPolicyLink from "../../documents/privacy_policy.pdf";
import { Button } from "components/Core";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";

const TherapistForm = () => {
  const { t, i18n } = useTranslation(["common", "forms"]);
  const textDirection = i18n.language === "en" ? "ltr" : "rtl";
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState({});
  const navigate = useNavigate();

  const getApiErrorMessage = (payload) => {
    if (!payload) return "An error occurred, Please try again";
    if (typeof payload === "string") return payload;
    return (
      payload.message ||
      payload.detail ||
      payload.error ||
      "An error occurred, Please try again"
    );
  };

  const alertSettings = {
    vertical: "bottom",
    horizontal: "left",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("forms:nameRequired")),
    email: Yup.string()
      .email(t("forms:emailInvalid"))
      .required(t("forms:emailRequired")),
    phoneNumber: Yup.string()
      .notRequired()
      .test("is-valid-phone", t("forms:phoneInvalid"), (value) => {
        if (!value) return true;
        return isValidPhoneNumber(`+${value}`);
      }),
    info: Yup.string(),
    termsAndConditions: Yup.boolean().oneOf(
      [true],
      t("forms:termsRequired")
    ),
    privacyPolicy: Yup.boolean().oneOf([true], t("forms:privacyRequired")),
    mailing: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNumber: "",
      info: "",
      termsAndConditions: false,
      privacyPolicy: false,
      mailing: true,
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const selectedLang = localStorage.getItem("i18nextLng");

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          full_name: values.name,
          email: values.email,
          additional_info: values.info,
          language: selectedLang,
          is_agree_to_receive_email: values.mailing,
          phone_number: values.phoneNumber ? `+${values.phoneNumber}` : "",
        }),
      };
      fetch(`${apiPath}/api/landing/join/`, requestOptions)
        .then(async (response) => {
          let payload = {};
          try {
            payload = await response.json();
          } catch {
            payload = {};
          }

          if (!response.ok) {
            throw payload;
          }

          return payload;
        })
        .then((result) => {
          if (result?.detail === "Ok") {
            setAnswer({ status: "Ok", text: "Message Sent" });
            setOpen(true);
            resetForm();
            setLoading(false);
            navigate("/thank-you");
            return;
          }

          setAnswer({ status: "error", text: getApiErrorMessage(result) });
          setOpen(true);
          setLoading(false);
        })
        .catch((error) => {
          setAnswer({ status: "error", text: getApiErrorMessage(error) });
          setOpen(true);
          setLoading(false);
        });
    },
  });

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <section className={Styles.wrapper}>
      <div className={Styles.form_wrapper} dir={textDirection}>
        <h1 className={Styles.title}>{t("common:buttons.becomeTherapist")}</h1>

        <form onSubmit={formik.handleSubmit} noValidate>
          <div className={Styles.form_item}>
            <label className={Styles.flex_col}>{t("forms:nameLabel")}</label>

            <input
              type="text"
              name="name"
              disabled={loading}
              placeholder={t("forms:namePlaceholder")}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name ? (
              <p className={Styles.error_message}>{formik.errors.name}</p>
            ) : null}
          </div>

          <div className={Styles.form_item}>
            <label className={Styles.flex_col}>{t("forms:emailLabel")}</label>

            <input
              type="email"
              name="email"
              disabled={loading}
              placeholder={t("forms:emailPlaceholder")}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email ? (
              <p className={Styles.error_message}>{formik.errors.email}</p>
            ) : null}
          </div>
          <div className={Styles.form_item}>
            <label className={Styles.flex_col}>
              {t("forms:phoneNumber")} ({t("forms:optional")})
            </label>

            <PhoneInput
              country={"bh"}
              enableSearch
              value={formik.values.phoneNumber}
              onChange={(phone) => formik.setFieldValue("phoneNumber", phone)}
              onBlur={() => formik.setFieldTouched("phoneNumber", true)}
              inputStyle={{
                minHeight: 50,
                width: "100%",
                fontFamily: "Poppins, sans-serif",
              }}
              containerStyle={{
                direction: "ltr",
              }}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
              <p className={Styles.error_message}>
                {formik.errors.phoneNumber}
              </p>
            ) : null}
          </div>
          <div className={Styles.form_item}>
            <label className={Styles.flex_col}>
              {t("forms:therapistInfoLabel")}
            </label>
            <textarea
              name="info"
              placeholder={t("forms:therapistInfoPlaceholder")}
              disabled={loading}
              rows="5"
              cols="55"
              value={formik.values.info}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className={Styles.form_item}>
            <p className={Styles.flex_ai_start}>
              <input
                type="checkbox"
                id="termsAndConditions"
                name="termsAndConditions"
                disabled={loading}
                checked={formik.values.termsAndConditions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className={Styles.label}>
                {textDirection === "ltr" ? (
                  <div>
                    I read and agree to the{" "}
                    <p onClick={() => window.open(termsOfUseLink, "_blank")}>
                      Terms of Use
                    </p>
                  </div>
                ) : (
                  <div>
                    لقد قرأت ووافقت على{" "}
                    <p onClick={() => window.open(termsOfUseLink, "_blank")}>
                      شروط الاستخدام
                    </p>
                  </div>
                )}
              </div>
            </p>
            {formik.touched.termsAndConditions &&
            formik.errors.termsAndConditions ? (
              <p className={Styles.error_message}>
                {formik.errors.termsAndConditions}
              </p>
            ) : null}
          </div>
          <div className={Styles.form_item}>
            <p className={Styles.flex_ai_start}>
              <input
                type="checkbox"
                id="privacyPolicy"
                name="privacyPolicy"
                disabled={loading}
                checked={formik.values.privacyPolicy}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className={Styles.label}>
                {textDirection === "ltr" ? (
                  <div>
                    I read, agree and consent to the processing of my personal
                    data in accordance with the{" "}
                    <p onClick={() => window.open(privacyPolicyLink, "_blank")}>
                      Privacy Policy
                    </p>
                  </div>
                ) : (
                  <div>
                    قرأت وأوافق على معالجة بياناتي الشخصية وفقًا{" "}
                    <p onClick={() => window.open(privacyPolicyLink, "_blank")}>
                      لسياسة الخصوصية
                    </p>
                  </div>
                )}
              </div>
            </p>
            {formik.touched.privacyPolicy && formik.errors.privacyPolicy ? (
              <p className={Styles.error_message}>
                {formik.errors.privacyPolicy}
              </p>
            ) : null}
          </div>

          <div className={Styles.form_item}>
            <p className={Styles.flex_ai_start}>
              <input
                type="checkbox"
                id="mailing"
                name="mailing"
                disabled={loading}
                checked={formik.values.mailing}
                onChange={formik.handleChange}
              />
              <label htmlFor="mailing" className={Styles.checkbox_label}>
                {t("forms:mailings")}
              </label>
            </p>
          </div>
          <div className={Styles.submit_container}>
            <Button
              minWidth={150}
              variant="smallCTA"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress color="info" size={15} />
              ) : (
                t("forms:sendEmail")
              )}
            </Button>
          </div>
        </form>

        <Snackbar
          anchorOrigin={alertSettings}
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={answer.status === "Ok" ? "success" : "error"}
            variant={"filled"}
            sx={{ width: "100%" }}
          >
            {answer.text}
          </Alert>
        </Snackbar>
      </div>

      <div className={Styles.image_wrapper}>
        <img src={slideImg} alt="slide" className={Styles.slide_img} />
      </div>
    </section>
  );
};

export default TherapistForm;
